import React, { useState } from 'react';
import { Lock, AlertTriangle, CheckCircle } from 'lucide-react';
import { changePassword } from '../../api/auth';
import C from '../../constants/colors';

export default function ChangePasswordModal({ onClose }) {
  const [current,  setCurrent]  = useState('');
  const [next,     setNext]     = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [error,    setError]    = useState('');
  const [done,     setDone]     = useState(false);
  const [loading,  setLoading]  = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (next !== confirm) { setError('New passwords do not match.'); return; }
    if (next.length < 8)  { setError('New password must be at least 8 characters.'); return; }
    if (next === current) { setError('New password must differ from the current one.'); return; }

    setLoading(true);
    try {
      await changePassword(current, next);
      setDone(true);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to change password.';
      setError(msg === 'Current password is incorrect' ? 'Current password is incorrect.' : msg);
    } finally {
      setLoading(false);
    }
  }

  const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    background: C.surfaceElevated, border: `1px solid ${C.border}`,
    color: C.textPrimary, fontSize: '14px', outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
    }}>
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: '16px', padding: '28px', width: '360px',
        fontFamily: "'DM Sans', sans-serif",
      }}>
        {done ? (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <CheckCircle size={40} color={C.green} style={{ display: 'block', margin: '0 auto 14px' }} />
            <h3 style={{ color: C.textPrimary, fontSize: '16px', fontWeight: 600, margin: '0 0 8px' }}>Password updated</h3>
            <p style={{ color: C.textSecondary, fontSize: '13px', margin: '0 0 20px' }}>
              Your password has been changed successfully.
            </p>
            <button onClick={onClose} style={{
              width: '100%', padding: '10px',
              background: C.blue, border: 'none', borderRadius: '8px',
              color: '#fff', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
            }}>Done</button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
              <Lock size={18} color={C.blue} />
              <h3 style={{ color: C.textPrimary, fontSize: '16px', fontWeight: 600, margin: 0 }}>Change password</h3>
            </div>

            {error && (
              <div style={{
                background: 'rgba(239,68,68,0.1)', border: `1px solid rgba(239,68,68,0.3)`,
                borderRadius: '8px', padding: '10px 12px',
                display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '16px',
              }}>
                <AlertTriangle size={14} color={C.red} style={{ flexShrink: 0, marginTop: '1px' }} />
                <span style={{ color: C.red, fontSize: '13px' }}>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', color: C.textSecondary, fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>
                  Current password
                </label>
                <input type="password" value={current} onChange={e => setCurrent(e.target.value)}
                  required autoFocus placeholder="••••••••" style={inputStyle} />
              </div>

              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', color: C.textSecondary, fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>
                  New password
                </label>
                <input type="password" value={next} onChange={e => setNext(e.target.value)}
                  required placeholder="Min 8 characters" style={inputStyle} />
              </div>

              <div style={{ marginBottom: '22px' }}>
                <label style={{ display: 'block', color: C.textSecondary, fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>
                  Confirm new password
                </label>
                <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
                  required placeholder="Repeat new password" style={inputStyle} />
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="button" onClick={onClose} style={{
                  flex: 1, padding: '10px',
                  background: 'transparent', border: `1px solid ${C.border}`,
                  borderRadius: '8px', color: C.textSecondary,
                  fontSize: '14px', fontWeight: 500, cursor: 'pointer',
                }}>Cancel</button>
                <button type="submit" disabled={loading} style={{
                  flex: 1, padding: '10px',
                  background: loading ? C.textMuted : C.blue,
                  border: 'none', borderRadius: '8px',
                  color: '#fff', fontSize: '14px', fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}>
                  {loading ? 'Saving…' : 'Update password'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
